// Ablauf eines Zugwechsels im Spielmodus: eigener Zug (mit Urteil des Coachs), Antwort des Gegners,
// Zurücknehmen. Die Partie selbst hält PlayCtrl.
import type { Key } from '@lichess-org/chessground/types';
import { buzz } from './appfeel';
import { t } from './i18n';
import { engineReply } from './playapi';
import type { PlayCtrl } from './playctrl';

/** Eigener Zug ist gespielt: Urteil abwarten (Fehler sofort zeigen) oder nur sammeln, dann der Gegner */
export async function afterOwnMove(c: PlayCtrl, before: string, last: [Key, Key]) {
  if (c.result) return;
  const game = c.game;
  const judged = c.coach.judge(before, c.fen, last[0] + last[1], c.moves[c.moves.length - 1].san);
  if (c.coach.instant) {
    c.coach.checking = true;
    c.redraw();
    const slip = await judged;
    if (game !== c.game) return;
    c.coach.checking = false;
    if (slip) {
      c.coach.pending = slip;
      c.redraw();
      return;
    }
  }
  void engineMove(c);
}

/** Pause vor dem zweiten Versuch, wenn der Server kurz nicht erreichbar war (Neustart) */
export const timing = { retryDelay: 2000 };

export async function engineMove(c: PlayCtrl) {
  const game = c.game;
  c.thinking = true;
  c.error = '';
  c.redraw();
  for (let attempt = 0; ; attempt++) {
    try {
      const r = await engineReply(c.fen, c.bot);
      if (game !== c.game) return; // inzwischen neue Partie oder aufgegeben
      c.apply(r.move, r.last);
      buzz();
      break;
    } catch (e) {
      if (game !== c.game) return;
      // vorübergehend: Server antwortet nicht (fetch: TypeError) oder nginx bremst (503/429); einmal nachfassen
      const status = (e as { status?: number }).status;
      const offline = e instanceof TypeError || status === 503 || status === 429;
      if (offline && attempt === 0) {
        await new Promise(r => setTimeout(r, timing.retryDelay));
        if (game !== c.game) return;
        continue;
      }
      c.error = offline ? t('Der Server ist gerade nicht erreichbar.') : (e as Error).message;
      break;
    }
  }
  c.thinking = false;
  c.redraw();
}

/** Gegenzug ist ausgeblieben (Fehler): noch einmal fragen */
export const canAskAgain = (c: PlayCtrl) => c.started && !c.result && !c.thinking && !c.coach.pending && c.pos.turn !== c.me;

export function askAgain(c: PlayCtrl) {
  if (canAskAgain(c)) void engineMove(c);
}

/** bis der Spieler wieder am Zug ist: ein Halbzug, wenn der Gegner noch nicht geantwortet hat, sonst zwei */
export function takeBackPlies(c: PlayCtrl): number {
  const n = c.pos.turn === c.me ? 2 : 1;
  const busy = c.thinking || c.coach.checking || c.resigned || !c.started;
  return busy || c.moves.length < n ? 0 : n;
}

export function takeBack(c: PlayCtrl) {
  const n = takeBackPlies(c);
  if (!n) return;
  c.game++;
  c.board.undo(n);
  c.result = null;
  c.outcome = null;
  c.coach.pending = null;
  c.coach.tookBack(c.moves.length);
  void c.coach.lookUp(c.fen, c.moves.length);
  c.redraw();
}
